import { useCallback, useState } from 'react';

import { updateStatus } from '@lumapps/contents/api/contents';
import { LegacyContentStatus } from '@lumapps/contents/types';
import type { ContentSave, ContentUpdate } from '@lumapps/designer-content-editor/types';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import type { ContentScheduleDialogProps } from '../components/ContentScheduleDialog';
import type { ContentSummaryDialogProps } from '../components/ContentSummaryDialog';
import { DESIGNER } from '../keys';
import { getContentDatesUpdate } from '../utils/getContentDatesUpdate';

type OpenScheduleDialogParams =
    /** With the publication summary flow, the dates are always followed by the default publication. */
    | { showSummary: true }
    | {
          showSummary: false;
          /** Action to run once the dates are confirmed, instead of publishing the content. */
          onDatesConfirmed?: () => unknown;
          /** Label of the schedule dialog confirm button. */
          confirmLabel?: string;
      };

export const useContentScheduling = ({
    updateContent,
    openSummaryDialog,
    publishContent,
    saveContent,
    contentId,
}: {
    contentId?: string;
    updateContent: ContentUpdate;
    saveContent: ContentSave;
    /** Default action once the dates are confirmed. Only needed by consumers using the summary flow. */
    publishContent?: () => void;
    /** Only needed by consumers opening the dialog with `showSummary: true`. */
    openSummaryDialog?: (props: Pick<ContentSummaryDialogProps, 'onConfirm' | 'publicationSourceType'>) => void;
}) => {
    const [isScheduleDialogOpen, , closeScheduleDialog, openDialog] = useBooleanState(false);
    const [shouldShowSummaryDialog, setShouldShowSummaryDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    // Action to run once the dates are confirmed (defaults to publishing the content).
    const [onDatesConfirmed, setOnDatesConfirmed] = useState<(() => unknown) | undefined>();
    const [confirmLabel, setConfirmLabel] = useState<string | undefined>();

    const { error, success } = useNotification();
    const { translateKey } = useTranslate();

    const openScheduleDialog = useCallback(
        (params: OpenScheduleDialogParams) => () => {
            setShouldShowSummaryDialog(params.showSummary);
            setOnDatesConfirmed(() => (params.showSummary ? undefined : params.onDatesConfirmed));
            setConfirmLabel(params.showSummary ? undefined : params.confirmLabel);
            openDialog();
        },
        [openDialog],
    );

    const handleConfirm: ContentScheduleDialogProps['onConfirm'] = async ({ startDate, endDate }) => {
        if (shouldShowSummaryDialog) {
            // We only update the temporary date inputs at first.
            // Real values will be updated on confirming the publish.
            const { startDateInput, endDateInput } = getContentDatesUpdate({ startDate, endDate });
            updateContent({
                content: { startDateInput, endDateInput },
                isPartial: true,
            });

            openSummaryDialog?.({
                publicationSourceType: 'schedule',
                onConfirm: () => {
                    // Before publishing, we update the startDate and endDate
                    updateContent({
                        content: {
                            startDate,
                            endDate,
                        },
                        isPartial: true,
                    });
                    publishContent?.();
                },
            });
            closeScheduleDialog();
        } else {
            setIsLoading(true);
            updateContent({
                content: getContentDatesUpdate({ startDate, endDate }),
                isPartial: true,
            });
            try {
                await (onDatesConfirmed ?? publishContent)?.();
                closeScheduleDialog();
            } catch (error) {
                // Error notification is handled by the action's own save/publish error handling;
                // the dialog is kept open so the user can retry.
            } finally {
                setIsLoading(false);
            }
        }
    };

    const unscheduleContent = async () => {
        if (!contentId) {
            return;
        }

        try {
            const { data: updatedContent } = await updateStatus({ contentId, status: LegacyContentStatus.draft });
            updateContent({ content: updatedContent, isPartial: false });
            await saveContent({ shouldNotTriggerLegacySuccess: true });
            success({ content: translateKey(DESIGNER.UNSCHEDULE_SUCCESS_MESSAGE) });
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    };

    return {
        openScheduleDialog,
        unscheduleContent,
        dialogProps: {
            isLoading,
            isOpen: isScheduleDialogOpen,
            onClose: closeScheduleDialog,
            onConfirm: handleConfirm,
            confirmLabel,
        },
    };
};
